import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, ChevronDown, CheckCircle2, Eye, MousePointerClick, Clock, AlertCircle, Mail } from "lucide-react";
import { getAuthHeaders } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const GROUP_LABELS = {
  A: "Insider-Report Lead",
  B: "Kontaktformular Lead",
  C: "Bereits kontaktiert",
  D: "Kunde",
};

const GROUP_COLORS = {
  A: "bg-amber-50 border-amber-200 text-amber-800",
  B: "bg-blue-50 border-blue-200 text-blue-800",
  C: "bg-purple-50 border-purple-200 text-purple-800",
  D: "bg-emerald-50 border-emerald-200 text-emerald-800",
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

/**
 * Mail-series panel: shows mail group + complete email history with
 * tracking (sent / opened / clicked) + manual send action.
 *
 * Props:
 *   kind        — "contacts" | "leads"
 *   contactId   — uuid of the contact / lead
 *   token       — admin token
 *   onGroupChange? — callback after group switch (optional)
 */
export default function MailSeriesPanel({ kind, contactId, token, onGroupChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // mail_id currently being sent
  const [showAllHistory, setShowAllHistory] = useState(false);

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${kind}/${contactId}/emails`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch {
      toast.error("Mail-Verlauf konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, contactId]);

  const changeGroup = async (group) => {
    if (!data || group === data.mail_group) return;
    if (!window.confirm(`Gruppe wirklich auf "${GROUP_LABELS[group]}" wechseln? Bereits gesendete Mails werden nicht erneut versendet.`)) return;
    try {
      await axios.patch(
        `${API}/admin/${kind}/${contactId}/group`,
        { mail_group: group },
        { headers: getAuthHeaders() },
      );
      toast.success(`Gruppe auf ${GROUP_LABELS[group]} gewechselt.`);
      await load();
      if (onGroupChange) onGroupChange(group);
    } catch {
      toast.error("Gruppenwechsel fehlgeschlagen.");
    }
  };

  const sendMail = async (mailId) => {
    if (!window.confirm(`Mail "${mailId}" jetzt versenden?`)) return;
    setBusy(mailId);
    try {
      await axios.post(
        `${API}/admin/${kind}/${contactId}/send-mail`,
        { mail_id: mailId },
        { headers: getAuthHeaders() },
      );
      toast.success(`${mailId} versendet.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="mt-6 pt-6 border-t border-[var(--al-line)] text-sm text-slate-500">
        Lade Mail-Verlauf…
      </div>
    );
  }
  if (!data) return null;

  const history = (data.history || []).slice().reverse(); // newest first
  const visibleHistory = showAllHistory ? history : history.slice(0, 5);
  const sentIds = new Set(history.filter((h) => ["sent", "skipped"].includes(h.status)).map((h) => h.mail_id));

  return (
    <div className="mt-6 pt-6 border-t border-[var(--al-line)]" data-testid="mail-series-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[var(--al-purple)]" />
          <h3 className="font-display font-semibold text-base">Mail-Serie &amp; Verlauf</h3>
        </div>
        {data.mail_group_changed_at && (
          <div className="text-[11px] text-slate-500 font-mono-tab">
            Sequenz-Start: {formatDate(data.mail_group_changed_at)}
          </div>
        )}
      </div>

      {/* Group selector */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Gruppe</div>
        <div className="flex flex-wrap gap-2" data-testid="mail-group-selector">
          {["A", "B", "C", "D"].map((g) => {
            const active = data.mail_group === g;
            return (
              <button
                key={g}
                data-testid={`mail-group-${g}`}
                onClick={() => changeGroup(g)}
                disabled={active}
                className={`px-3 py-1.5 rounded-full border text-xs font-display font-semibold transition-all ${
                  active
                    ? GROUP_COLORS[g] + " ring-2 ring-offset-1 ring-current cursor-default"
                    : "bg-white border-[var(--al-line)] text-slate-600 hover:border-slate-400"
                }`}
              >
                <span className="font-mono-tab font-bold mr-1">{g}</span>
                {GROUP_LABELS[g]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Schedule overview */}
      <details className="mb-5 group" data-testid="mail-schedule-details">
        <summary className="cursor-pointer text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 list-none flex items-center gap-1.5 hover:text-slate-700">
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          Sequenz für Gruppe {data.mail_group} ({(data.schedule || []).length} Mails)
        </summary>
        <div className="mt-3 rounded-xl border border-[var(--al-line)] overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">ID</th>
                <th className="text-left px-3 py-2 font-semibold">Tag</th>
                <th className="text-left px-3 py-2 font-semibold">Betreff</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--al-line)]">
              {(data.schedule || []).map((s) => {
                const sent = sentIds.has(s.mail_id);
                return (
                  <tr key={s.mail_id} data-testid={`schedule-row-${s.mail_id}`}>
                    <td className="px-3 py-2 font-mono-tab font-semibold text-slate-700">{s.mail_id}</td>
                    <td className="px-3 py-2 text-slate-600">
                      Tag {s.day}{s.kind === "recurring" && s.interval ? ` (alle ${s.interval}T)` : ""}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{s.subject}</td>
                    <td className="px-3 py-2">
                      {sent ? (
                        <span className="text-emerald-600 inline-flex items-center gap-1 font-medium"><CheckCircle2 className="h-3 w-3" /> Gesendet</span>
                      ) : (
                        <span className="text-slate-400 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Wartend</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!sent && (
                        <button
                          onClick={() => sendMail(s.mail_id)}
                          disabled={busy === s.mail_id}
                          data-testid={`send-now-${s.mail_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--al-purple)]/10 text-[var(--al-purple)] hover:bg-[var(--al-purple)]/20 text-[11px] font-semibold transition-colors disabled:opacity-50"
                        >
                          <Send className="h-3 w-3" />
                          {busy === s.mail_id ? "Sendet…" : "Jetzt senden"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>

      {/* Email history */}
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Tatsächlich versendet ({history.length})
        </div>
        {history.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--al-line)] p-5 text-center text-sm text-slate-400">
            Noch keine Mails an diesen Kontakt versendet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="mail-history-list">
            {visibleHistory.map((h, idx) => (
              <li
                key={`${h.mail_id}-${h.sent_at || idx}`}
                className="rounded-xl border border-[var(--al-line)] p-3 bg-white"
                data-testid={`history-${h.mail_id}-${idx}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-tab text-xs font-bold text-slate-700">{h.mail_id}</span>
                      <span className="text-sm font-medium text-slate-900 truncate">{h.subject}</span>
                    </div>
                    {/* Event timeline: sent → opened → clicked */}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono-tab">
                      {h.status === "scheduled" && h.scheduled_at && (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Clock className="h-3 w-3" />
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Geplant für</span>
                          <span className="text-slate-500">{formatDate(h.scheduled_at)}</span>
                        </span>
                      )}
                      {h.sent_at && h.status !== "scheduled" && (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Versendet</span>
                          <span className="text-slate-500">{formatDate(h.sent_at)}</span>
                        </span>
                      )}
                      {h.opened_at && (
                        <span className="inline-flex items-center gap-1 text-blue-700">
                          <Eye className="h-3 w-3" />
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Geöffnet</span>
                          <span className="text-slate-500">{formatDate(h.opened_at)}</span>
                        </span>
                      )}
                      {h.clicked_at && (
                        <span className="inline-flex items-center gap-1 text-purple-700">
                          <MousePointerClick className="h-3 w-3" />
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Geklickt</span>
                          <span className="text-slate-500">{formatDate(h.clicked_at)}</span>
                        </span>
                      )}
                      {h.delivered_at && !h.opened_at && (
                        <span className="inline-flex items-center gap-1 text-slate-500">
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Zugestellt</span>
                          <span>{formatDate(h.delivered_at)}</span>
                        </span>
                      )}
                      {h.bounced_at && (
                        <span className="inline-flex items-center gap-1 text-rose-600">
                          <AlertCircle className="h-3 w-3" />
                          <span className="font-semibold uppercase tracking-wider text-[9px]">Bounce</span>
                          <span>{formatDate(h.bounced_at)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] shrink-0">
                    {h.status === "failed" && (
                      <span className="inline-flex items-center gap-1 text-rose-600 font-semibold"><AlertCircle className="h-3 w-3" /> Fehler</span>
                    )}
                    {h.status === "skipped" && (
                      <span className="inline-flex items-center gap-1 text-slate-500">Übersprungen</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {history.length > 5 && (
          <button
            onClick={() => setShowAllHistory((v) => !v)}
            data-testid="toggle-history"
            className="mt-3 text-xs font-semibold text-[var(--al-purple)] hover:underline"
          >
            {showAllHistory ? "Weniger anzeigen" : `Alle ${history.length} anzeigen`}
          </button>
        )}
      </div>
    </div>
  );
}
